import React from "react";

import { format } from "date-fns";

import { HouseholdWithBalances } from "@fartherfinance/frontend/api/Households/types";

import { filterMembers, filterOwners } from "../utils";
import { HouseholdColumnDefinitions } from "@src/multiCustodian/hooks/Advisor/useTableRows";
import { Cell, Row } from "@src/sharedComponents/Table/Types";
import toUSD from "@src/utils/toUSD";

import HouseholdMembersLink from "./HouseholdMembersLink";
import HouseholdNameLink from "./HouseholdNameLink";

export const TableKeys = [
  "Household",
  "Owner",
  "Members",
  "AUM",
  "Advisor",
  "Date Established",
] as const;

export type TableKeysType = typeof TableKeys[number];

export const householdTableColumnDefs: HouseholdColumnDefinitions<TableKeysType> =
  {
    Household: {
      empty: { value: "No households found" },
      displayData: (household: HouseholdWithBalances) => ({
        value: (
          <HouseholdNameLink
            householdId={household.householdId}
            householdName={household.name}
          />
        ),
      }),
    },
    Owner: {
      displayData: (household: HouseholdWithBalances) => ({
        value: (
          <HouseholdMembersLink
            members={filterOwners(household.members ?? [])}
          />
        ),
      }),
    },
    Members: {
      displayData: (household: HouseholdWithBalances) => ({
        value: (
          <HouseholdMembersLink
            members={filterMembers(household.members ?? [])}
          />
        ),
      }),
    },
    AUM: {
      displayData: (household: HouseholdWithBalances) => ({
        value: toUSD(
          household.totalAssets ? household.totalAssets.toNumber() : 0
        ),
      }),
    },
    Advisor: {
      displayData: (household: HouseholdWithBalances) =>
        `${household.advisorFirstName ?? "-"} ${
          household.advisorLastName ?? " -"
        }`,
    },
    "Date Established": {
      displayData: (household: HouseholdWithBalances) => ({
        value: household.establishedAt
          ? format(new Date(household.establishedAt), "M/d/yyyy")
          : "--",
        fullValue: household.establishedAt || "",
      }),
    },
  } as const;

export function createDisplayDataRow(
  household: HouseholdWithBalances,
  openHouseholdDetails: (householdId: string) => void
): Row<TableKeysType, Cell> {
  const baseRow = {
    key: household.householdId,
    onClick: () => openHouseholdDetails(household.householdId),
  };

  const columnData = Object.keys(householdTableColumnDefs).reduce(
    (acc, key) => {
      const columnKey = key as keyof typeof householdTableColumnDefs;
      acc[key as TableKeysType] =
        householdTableColumnDefs[columnKey].displayData(household);

      return acc;
    },
    {} as Record<TableKeysType, Cell | string>
  );

  return { ...baseRow, ...columnData } as Row<TableKeysType, Cell>;
}
